/**
 * Línea de tiempo de eventos del lote (Sprint 18 P7). Presentacional:
 * recibe los eventos y los agrupa visualmente por fecha. Compartido entre
 * la pantalla del vecino ("Mi historial") y la vista de comisión.
 */
import type { EventoHistorial } from '../lib/api/lotes';
import { formatFecha } from '../lib/format';
import { Card } from './ui/Card';
import { Pill } from './ui/Pill';
import { EmptyState } from './ui/EmptyState';

// Mapeo tipo de evento → variante de Pill + etiqueta corta.
const META: Record<string, { variant: 'success' | 'pending' | 'danger' | 'info' | 'warning'; label: string }> = {
  tenencia_abierta: { variant: 'info', label: 'Tenencia' },
  tenencia_cerrada: { variant: 'warning', label: 'Tenencia' },
  residente_inscrito: { variant: 'info', label: 'Residente' },
  residente_validado: { variant: 'success', label: 'Residente' },
  residente_baja: { variant: 'warning', label: 'Residente' },
  vehiculo_inscrito: { variant: 'info', label: 'Vehículo' },
  vehiculo_validado: { variant: 'success', label: 'Vehículo' },
  vehiculo_baja: { variant: 'warning', label: 'Vehículo' },
  recurso_entregado: { variant: 'success', label: 'Recurso' },
  recurso_estado: { variant: 'warning', label: 'Recurso' },
  pago_cuota: { variant: 'success', label: 'Pago' },
};

interface Props {
  eventos: EventoHistorial[];
}

export function HistorialTimeline({ eventos }: Props) {
  if (eventos.length === 0) {
    return <EmptyState title="Aún no hay eventos en el historial de esta casa" />;
  }

  // Agrupar por fecha (los eventos ya vienen ordenados desc del backend).
  const grupos: { fecha: string; items: EventoHistorial[] }[] = [];
  for (const ev of eventos) {
    const ultimo = grupos[grupos.length - 1];
    if (ultimo && ultimo.fecha === ev.fecha) {
      ultimo.items.push(ev);
    } else {
      grupos.push({ fecha: ev.fecha, items: [ev] });
    }
  }

  return (
    <div className="space-y-4">
      {grupos.map((g) => (
        <div key={g.fecha}>
          <div className="text-xs uppercase tracking-wide text-text-muted mb-2">
            {formatFecha(g.fecha)}
          </div>
          <Card>
            <ul className="space-y-2">
              {g.items.map((ev, i) => {
                const meta = META[ev.tipo] ?? { variant: 'info' as const, label: 'Evento' };
                return (
                  <li
                    key={`${ev.tipo}-${ev.descripcion}-${i}`}
                    className="flex items-start gap-2 text-sm text-text-primary"
                  >
                    <Pill variant={meta.variant}>{meta.label}</Pill>
                    <span className="pt-0.5">{ev.descripcion}</span>
                  </li>
                );
              })}
            </ul>
          </Card>
        </div>
      ))}
    </div>
  );
}
